import React, { useRef, useState, useCallback } from 'react';
import { Button } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import { useUploadProceduresMutation } from '../../../../services/ConfigurationService';
import { useSignalR } from '../../../../hooks/useSignalR';
import { UploadProgress } from '../../../../services/signalRService';

interface ProcedureUploadCsvProps {
  company: string;
  centerId: string;
  disabled?: boolean;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onProgressUpdate?: (progress: UploadProgress) => void;
  onComplete?: (progress: UploadProgress) => void;
}

const ProcedureUploadCsv: React.FC<ProcedureUploadCsvProps> = ({
  company,
  centerId,
  disabled,
  onUploadStart,
  onUploadComplete,
  onSuccess,
  onError,
  onProgressUpdate,
  onComplete
}) => {
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadProcedures, { isLoading: isUploading, error: uploadError }] = useUploadProceduresMutation();

  // SignalR callbacks
  const onProgress = useCallback((progress: UploadProgress) => {
    console.log('Procedure upload progress:', progress);
    onProgressUpdate?.(progress);
  }, [onProgressUpdate]);

  const onCompleted = useCallback((progress: UploadProgress) => {
    console.log('Procedure upload completed:', progress);
    setIsUploadInProgress(false);
    onUploadComplete?.();
    onComplete?.(progress);
    
    if (progress.status === 'Completed') {
      onSuccess?.();
    }
    // Note: For failed uploads, let onComplete handle the error display
    // Don't call onError here as it would override the detailed progress message
  }, [onUploadComplete, onComplete, onSuccess]);

  const onErrorCallback = useCallback((progress: UploadProgress) => {
    console.log('Procedure upload error:', progress);
    setIsUploadInProgress(false);
    onUploadComplete?.();
    // Update progress state with detailed error information instead of calling onError
    onProgressUpdate?.(progress);
  }, [onUploadComplete, onProgressUpdate]);

  // Setup SignalR connection for Configuration service with Procedure uploadType
  const { isConnected } = useSignalR({
    serviceName: 'Configuration',
    uploadType: 'Procedure', // Match the UploadType constant in backend
    onProgress,
    onCompleted,
    onError: onErrorCallback,
    autoConnect: true
  });

  const handleUploadClick = () => {
    if (!company || !centerId) {
      console.warn('Company and Center ID are required');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && company && centerId) {
      try {
        // Reset progress state and start tracking
        setIsUploadInProgress(true);
        onUploadStart?.();

        const result = await uploadProcedures({
          company,
          centerId,
          file: file
        }).unwrap();
        
        // Show upload results
        const message = `Upload completed: ${result.successful} successful, ${result.failed} failed`;
        console.log(message, result);
        
        // The completion will be handled by SignalR callback
        // But handle the case where SignalR might not be connected
        if (!isConnected) {
          setIsUploadInProgress(false);
          onUploadComplete?.();
          
          if (result.isSuccess) {
            onSuccess?.();
          } else {
            onError?.(result.errorMessage || 'Upload completed with errors');
          }
        }
      } catch (err: any) {
        console.error('Upload failed:', err);
        setIsUploadInProgress(false);
        onUploadComplete?.();
        onError?.(err?.data?.message || err?.message || 'Upload failed');
      }
    }
    
    // Clear the input so the same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isDisabled = disabled || !company || !centerId || isUploading || isUploadInProgress;

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".csv"
        style={{ display: 'none' }}
      />
      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={handleUploadClick}
        size="small"
        disabled={isDisabled}
      >
        Upload CSV
      </Button>
    </>
  );
};

export default ProcedureUploadCsv;